/* VoiceAgent — mic-driven realtime voice assistant for the app.
   ──────────────────────────────────────────────────────────────
   Ported (genericized) from a production voice-agent kit. The proven
   plumbing is kept verbatim where possible:

     - PCM16 ↔ Float32 audio conversion + scheduled play queue
     - Realtime WebSocket lifecycle (session.update handshake, VAD
       events, barge-in cancel, transcript deltas, tool-call loop)
     - Frequency-bar visualiser driven by an AnalyserNode

   The flow:
     1. POST {API}/voice/session (Firebase auth) → ephemeral token +
        wsUrl/wsProtocols + session config. The real provider API key
        never reaches this code.
     2. Open a WebSocket DIRECTLY to the provider; stream mic PCM up,
        play PCM deltas back. Mic capture runs in an AudioWorklet
        (ScriptProcessor fallback for older browsers). An unexpected
        socket drop auto-reconnects with a fresh ephemeral token.
     3. Tool calls: client tools (navigate_app) run in the browser via
        react-router; everything else POSTs to {API}/voice/tool and the
        result is fed back into the conversation.

   Text chat: the panel also has a text input. During a live voice
   session typed messages are injected into the realtime conversation;
   otherwise they go to {API}/voice/chat (same tool registry, no audio).

   Renders nothing unless the user is signed in and REACT_APP_UPLOAD_API
   is configured. */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import firebase from '../firebaseConfig';
import { captureScreenOutline, screenContextItem } from '../utils/screenOutline';
import { runPageControl, isPageControlTool } from '../utils/pageControls';
import { isKnownRoute } from '../utils/appRoutes';
import '../utils/pageControls.css';
import './VoiceAgent.css';

const API = process.env.REACT_APP_UPLOAD_API || '';
const SAMPLE_RATE = 24000;
const NUM_BARS = 20;
const ATTACK = 0.12;
const DECAY = 0.45;
const MIC_CHUNK = 4096; // samples per chunk sent upstream (~170ms @ 24kHz)
const MAX_MIC_BUFFER = 120; // pre-handshake/reconnect buffer cap (~20s)
const MAX_RECONNECTS = 2;

/* Mic capture worklet — buffers MIC_CHUNK samples then posts them to the
   main thread. Loaded from a Blob URL so CRA doesn't need to serve a file. */
const MIC_WORKLET_SRC = `
registerProcessor('va-mic-capture', class extends AudioWorkletProcessor {
  constructor() {
    super();
    this.buf = new Float32Array(${MIC_CHUNK});
    this.len = 0;
  }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (!ch) return true;
    let off = 0;
    while (off < ch.length) {
      const n = Math.min(ch.length - off, this.buf.length - this.len);
      this.buf.set(ch.subarray(off, off + n), this.len);
      this.len += n;
      off += n;
      if (this.len === this.buf.length) {
        this.port.postMessage(this.buf.slice(0));
        this.len = 0;
      }
    }
    return true;
  }
});
`;

function float32ToBase64PCM16(f32) {
  const pcm16 = new Int16Array(f32.length);
  for (let i = 0; i < f32.length; i++) {
    const s = Math.max(-1, Math.min(1, f32[i]));
    pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  const bytes = new Uint8Array(pcm16.buffer);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

async function authHeaders() {
  const user = firebase.auth().currentUser;
  const token = user ? await user.getIdToken() : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function VoiceAgent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [voiceActive, setVoiceActive] = useState(false);
  const [voiceState, setVoiceState] = useState('idle');
  const [muted, setMuted] = useState(false);
  const [speakerMuted, setSpeakerMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [streamingText, setStreamingText] = useState('');
  const [error, setError] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [chatInput, setChatInput] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [activeTool, setActiveTool] = useState(''); // tool currently executing (shown in header/pill)
  const [pttMode, setPttMode] = useState(false); // push-to-talk: hold Space to speak
  const [panelPos, setPanelPos] = useState(() => {
    try { return JSON.parse(localStorage.getItem('va-panel-pos') || 'null'); } catch { return null; }
  });

  const wsRef = useRef(null);
  const streamRef = useRef(null);
  const captureCtxRef = useRef(null);
  const playCtxRef = useRef(null);
  const playGainRef = useRef(null);
  const processorRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const barsRef = useRef(null);
  const prevLevelsRef = useRef(new Float32Array(NUM_BARS));
  const sessionReadyRef = useRef(false);
  // What the user sees, re-sent to the live session whenever the route
  // changes (navigate_app, a tab click, a page control) so the model
  // narrates the screen that is actually there — never the previous one.
  const sendScreen = useCallback(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !sessionReadyRef.current) return;
    const outline = captureScreenOutline({ maxChars: 2500 });
    const text = screenContextItem(outline, document.title, `${window.location.pathname}${window.location.search}`);
    if (!text) return;
    try {
      ws.send(JSON.stringify({ type: 'conversation.item.create', item: { type: 'message', role: 'system', content: [{ type: 'input_text', text }] } }));
    } catch (_) {}
  }, []);
  useEffect(() => {
    const t = setTimeout(sendScreen, 700); // let the new page mount
    return () => clearTimeout(t);
  }, [location.pathname, location.search, sendScreen]);
  const micBufferRef = useRef([]);
  const playQueueRef = useRef([]);
  const nextPlayTimeRef = useRef(0);
  const currentSourceRef = useRef(null);
  const activeResponseIdRef = useRef(null);
  const responseHadToolCallsRef = useRef(false);
  const pendingToolsRef = useRef(0); // tool executions still in flight
  const followupWantedRef = useRef(false); // response.done arrived before tools finished
  const agentPartialRef = useRef('');
  const abortedRef = useRef(false);
  const voiceStateRef = useRef('idle');
  const sessionStartRef = useRef(0);
  const elapsedTimerRef = useRef(null);
  const scrollRef = useRef(null);
  const mutedRef = useRef(false);
  const speakerMutedRef = useRef(false);
  const volumeRef = useRef(1);
  const reconnectsRef = useRef(0);
  const messagesRef = useRef([]); // mirror for save-on-stop
  const thinkingSinceRef = useRef(0); // when the current thinking/processing stretch began
  const watchdogRef = useRef(null); // stall watchdog after tool follow-ups
  const watchdogFiresRef = useRef(0);
  const dragRef = useRef(null);
  const duckedRef = useRef(false); // playback suspended pending barge-in verdict
  const duckTimerRef = useRef(null); // failsafe: auto-resume if speech_stopped never comes
  const bargeStartRef = useRef(0);
  const bargeAudioStartRef = useRef(null); // provider audio_start_ms if given
  const bargeMinSpeechMsRef = useRef(400); // sub-this speech = noise, resume

  useEffect(() => { voiceStateRef.current = voiceState; }, [voiceState]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streamingText]);

  const setVState = useCallback((s) => {
    const wasWaiting = voiceStateRef.current === 'processing' || voiceStateRef.current === 'thinking';
    const isWaiting = s === 'processing' || s === 'thinking';
    if (isWaiting && !wasWaiting) thinkingSinceRef.current = Date.now();
    voiceStateRef.current = s;
    setVoiceState(s);
  }, []);

  /* ── Tool dispatch ─────────────────────────────────────────────
     Client tools run in the browser; everything else goes to the
     backend's allowlisted /voice/tool endpoint. */
  const executeTool = useCallback(async (name, args) => {
    if (name === 'navigate_app') {
      const path = String(args?.path || '/');
      // Same guard as the server's Nova catalog (appCatalog.isKnownRoute):
      // only screens this app has — permission lives here, not in the prompt.
      if (isKnownRoute(path)) {
        navigate(path);
        return { ok: true, navigatedTo: path };
      }
      return { error: `"${path}" is not a screen of this app` };
    }
    if (isPageControlTool(name)) {
      // The agent's hands (utils/pageControls.js): refusals come back with a
      // spoken-ready reason; the screen is re-sent after the route settles.
      try { return await runPageControl(name, args || {}); } catch (err) { return { ok: false, error: err.message }; }
    }
    try {
      const res = await fetch(`${API}/voice/tool`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({ tool: name, params: args }),
      });
      const data = await res.json();
      if (!res.ok) return { error: data.error || `tool failed (${res.status})` };
      return data.result;
    } catch (err) {
      return { error: err.message };
    }
  }, [navigate]);

  /* ── Audio output (scheduled PCM queue + gain for speaker mute/volume) ── */
  const applyOutputGain = useCallback(() => {
    if (playGainRef.current) {
      playGainRef.current.gain.value = speakerMutedRef.current ? 0 : volumeRef.current;
    }
  }, []);

  const stopPlayback = useCallback(() => {
    playQueueRef.current = [];
    if (playGainRef.current && playCtxRef.current) {
      try { playGainRef.current.disconnect(); } catch (_) {}
      const gain = playCtxRef.current.createGain();
      gain.connect(playCtxRef.current.destination);
      playGainRef.current = gain;
      applyOutputGain();
    }
    if (currentSourceRef.current) {
      try { currentSourceRef.current.stop(); } catch (_) {}
      currentSourceRef.current = null;
    }
    nextPlayTimeRef.current = 0;
  }, [applyOutputGain]);

  const drainPlayQueue = useCallback(() => {
    if (!playCtxRef.current || playQueueRef.current.length === 0) return;
    const ctx = playCtxRef.current;
    const dest = playGainRef.current || ctx.destination;
    while (playQueueRef.current.length > 0) {
      const chunk = playQueueRef.current.shift();
      const buffer = ctx.createBuffer(1, chunk.length, SAMPLE_RATE);
      buffer.getChannelData(0).set(chunk);
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(dest);
      const startAt = Math.max(ctx.currentTime, nextPlayTimeRef.current);
      source.start(startAt);
      nextPlayTimeRef.current = startAt + buffer.duration;
      currentSourceRef.current = source;
    }
  }, []);

  const playBase64Audio = useCallback((base64Data) => {
    try {
      const binStr = atob(base64Data);
      const bytes = new Uint8Array(binStr.length);
      for (let i = 0; i < binStr.length; i++) bytes[i] = binStr.charCodeAt(i);
      const pcm16 = new Int16Array(bytes.buffer);
      const f32 = new Float32Array(pcm16.length);
      for (let i = 0; i < pcm16.length; i++) f32[i] = pcm16[i] / 32768.0;
      playQueueRef.current.push(f32);
      drainPlayQueue();
    } catch (err) {
      console.warn('[VoiceAgent] audio decode error:', err.message);
    }
  }, [drainPlayQueue]);

  /* ── Mic capture ───────────────────────────────────────────────── */
  const handleMicChunk = useCallback((f32) => {
    if (mutedRef.current) return; // muted: send nothing, not silence
    if (!sessionReadyRef.current) {
      if (micBufferRef.current.length < MAX_MIC_BUFFER) micBufferRef.current.push(f32);
      return;
    }
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: 'input_audio_buffer.append', audio: float32ToBase64PCM16(f32) }));
  }, []);

  const attachMic = useCallback(async (ctx, micStream) => {
    const source = ctx.createMediaStreamSource(micStream);
    let node;
    try {
      const url = URL.createObjectURL(new Blob([MIC_WORKLET_SRC], { type: 'application/javascript' }));
      try { await ctx.audioWorklet.addModule(url); } finally { URL.revokeObjectURL(url); }
      node = new AudioWorkletNode(ctx, 'va-mic-capture', {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        outputChannelCount: [1],
      });
      node.port.onmessage = (e) => handleMicChunk(e.data);
    } catch (err) {
      console.warn('[VoiceAgent] AudioWorklet unavailable, using ScriptProcessor:', err.message);
      node = ctx.createScriptProcessor(MIC_CHUNK, 1, 1);
      node.onaudioprocess = (e) => handleMicChunk(new Float32Array(e.inputBuffer.getChannelData(0)));
    }
    source.connect(node);
    node.connect(ctx.destination); // keeps the node pulled; output is silent
    processorRef.current = node;
  }, [handleMicChunk]);

  /* ── Visualiser ────────────────────────────────────────────────── */
  const startVisualiser = useCallback((ctx, micStream) => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    try { analyserRef.current && analyserRef.current.disconnect(); } catch (_) {}
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 128;
    analyser.smoothingTimeConstant = 0.3;
    ctx.createMediaStreamSource(micStream).connect(analyser);
    analyserRef.current = analyser;
    const bins = analyser.frequencyBinCount;
    const data = new Uint8Array(bins);
    const prev = prevLevelsRef.current;
    let t = 0;
    const tick = () => {
      analyser.getByteFrequencyData(data);
      const isSpeaking = voiceStateRef.current === 'speaking';
      t += 0.08;
      const step = Math.max(1, Math.floor(bins / NUM_BARS));
      const bars = barsRef.current?.children;
      for (let i = 0; i < NUM_BARS; i++) {
        const idx = Math.min(i * step, bins - 1);
        let raw = data[idx] / 255;
        if (isSpeaking) {
          const wave = 0.3 + 0.25 * Math.sin(t + i * 0.35) + 0.15 * Math.sin(t * 1.7 + i * 0.2) + 0.1 * Math.random();
          raw = Math.max(raw, wave);
        }
        const smooth = raw > prev[i] ? ATTACK : DECAY;
        const val = prev[i] * smooth + raw * (1 - smooth);
        prev[i] = val;
        if (bars && bars[i]) bars[i].style.height = `${Math.max(2, val * 20)}px`;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const stopVisualiser = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    try { analyserRef.current && analyserRef.current.disconnect(); } catch (_) {}
    analyserRef.current = null;
    prevLevelsRef.current = new Float32Array(NUM_BARS);
    const bars = barsRef.current?.children;
    if (bars) for (let i = 0; i < bars.length; i++) bars[i].style.height = '2px';
  }, []);

  /* ── Session lifecycle ─────────────────────────────────────────── */

  /* Persist the transcript + health counters at session end (fire-and-forget;
     audio streams direct to the provider, so the client is the only place the
     conversation exists). Powers voiceSessions review + the health card. */
  const saveTranscript = useCallback(() => {
    const msgs = messagesRef.current;
    if (!msgs.length || !sessionStartRef.current) return;
    const payload = {
      startedAt: sessionStartRef.current,
      endedAt: Date.now(),
      reconnects: reconnectsRef.current,
      watchdogFires: watchdogFiresRef.current,
      route: window.location.pathname,
      messages: msgs.slice(-120).map((m) => ({ role: m.role, content: String(m.content).slice(0, 2000) })),
    };
    (async () => {
      try {
        await fetch(`${API}/voice/transcript`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
          body: JSON.stringify(payload),
        });
      } catch (_) {}
    })();
  }, []);

  const stopVoice = useCallback(() => {
    saveTranscript();
    abortedRef.current = true;
    if (watchdogRef.current) { clearTimeout(watchdogRef.current); watchdogRef.current = null; }
    watchdogFiresRef.current = 0;
    duckedRef.current = false;
    if (duckTimerRef.current) { clearTimeout(duckTimerRef.current); duckTimerRef.current = null; }
    setActiveTool('');
    stopVisualiser();
    if (elapsedTimerRef.current) { clearInterval(elapsedTimerRef.current); elapsedTimerRef.current = null; }
    if (processorRef.current) { try { processorRef.current.disconnect(); } catch (_) {} processorRef.current = null; }
    if (wsRef.current) { try { wsRef.current.close(); } catch (_) {} wsRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    if (captureCtxRef.current) { captureCtxRef.current.close().catch(() => {}); captureCtxRef.current = null; }
    if (playGainRef.current) { try { playGainRef.current.disconnect(); } catch (_) {} playGainRef.current = null; }
    if (playCtxRef.current) { playCtxRef.current.close().catch(() => {}); playCtxRef.current = null; }
    sessionReadyRef.current = false;
    activeResponseIdRef.current = null;
    micBufferRef.current = [];
    playQueueRef.current = [];
    nextPlayTimeRef.current = 0;
    reconnectsRef.current = 0;
    setVoiceActive(false);
    setVState('idle');
    setMuted(false);
    mutedRef.current = false;
    setStreamingText('');
    setElapsed(0);
    sessionStartRef.current = 0;
    agentPartialRef.current = '';
  }, [saveTranscript, stopVisualiser, setVState]);

  const fetchSession = useCallback(async () => {
    const r = await fetch(`${API}/voice/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
      body: JSON.stringify({}),
    });
    if (!r.ok) {
      let detail = '';
      try { detail = (await r.json()).error || ''; } catch (_) {}
      throw new Error(detail || `session failed (${r.status})`);
    }
    return r.json();
  }, []);

  /* Opens the realtime WebSocket for a fetched session. Named so the
     reconnect path inside onclose can call it again with a fresh token. */
  const clearWatchdog = useCallback(() => {
    if (watchdogRef.current) { clearTimeout(watchdogRef.current); watchdogRef.current = null; }
  }, []);

  /* End a duck: restore normal volume and drop the failsafe. Safe anytime. */
  const unduck = useCallback(() => {
    if (duckTimerRef.current) { clearTimeout(duckTimerRef.current); duckTimerRef.current = null; }
    duckedRef.current = false;
    applyOutputGain();
    // Legacy safety: earlier builds suspended the context; never stay there.
    if (playCtxRef.current && playCtxRef.current.state === 'suspended') {
      playCtxRef.current.resume().catch(() => {});
    }
  }, [applyOutputGain]);

  /* Stall watchdog: if no response starts within 15s of a follow-up request,
     re-request once; if still nothing, tell the user instead of dead air. */
  const armWatchdog = useCallback(function arm(attempt = 0) {
    clearWatchdog();
    watchdogRef.current = setTimeout(() => {
      watchdogRef.current = null;
      if (abortedRef.current || activeResponseIdRef.current) return; // a response arrived
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
      watchdogFiresRef.current += 1;
      if (attempt === 0) {
        try { wsRef.current.send(JSON.stringify({ type: 'response.create' })); } catch (_) {}
        arm(1);
      } else {
        setMessages((prev) => [...prev, { id: `w-${Date.now()}`, role: 'tool', content: '✕ the assistant stalled — say something or type below to retry' }]);
        setVState('listening');
      }
    }, 15000);
  }, [clearWatchdog, setVState]);

  /* Request the model's follow-up narration AFTER tool outputs are in the
     conversation and the audio queue has drained. Never call this while a
     tool execution is still in flight — a response.create issued before the
     function_call_output lands makes the model answer without the data, and
     the late output then never gets narrated (the original silent-hang bug). */
  const scheduleFollowup = useCallback(() => {
    const ctx = playCtxRef.current;
    const drainMs = ctx ? Math.max(0, (nextPlayTimeRef.current - ctx.currentTime) * 1000) + 50 : 50;
    setVState('thinking');
    setTimeout(() => {
      try {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'response.create' }));
          armWatchdog(0);
        }
      } catch (_) {}
    }, drainMs);
  }, [armWatchdog, setVState]);

  const connectWs = useCallback(function connect(sessionData) {
    const { ephemeralToken, wsUrl, wsProtocols, sessionConfig, clientTuning } = sessionData;
    if (!ephemeralToken) throw new Error('no ephemeral token from /voice/session');
    if (clientTuning && Number(clientTuning.bargeMinSpeechMs) > 0) {
      bargeMinSpeechMsRef.current = Number(clientTuning.bargeMinSpeechMs);
    }
    sessionReadyRef.current = false;
    const ws = new WebSocket(wsUrl, wsProtocols);
    wsRef.current = ws;
    ws.onopen = () => {
      try { ws.send(JSON.stringify({ type: 'session.update', session: sessionConfig })); } catch (_) {}
    };
    ws.onmessage = async (e) => {
      let evt;
      try { evt = JSON.parse(e.data); } catch { return; }
      switch (evt.type) {
        case 'session.updated':
          sessionReadyRef.current = true;
          reconnectsRef.current = 0;
          setError(null);
          setVState('listening');
          setTimeout(sendScreen, 300);
          if (micBufferRef.current.length > 0 && ws.readyState === WebSocket.OPEN) {
            micBufferRef.current.forEach((chunk) => {
              ws.send(JSON.stringify({ type: 'input_audio_buffer.append', audio: float32ToBase64PCM16(chunk) }));
            });
            micBufferRef.current = [];
          }
          break;
        case 'error':
          setError((evt.error && (evt.error.message || evt.error.code)) || 'Voice service error');
          break;
        case 'input_audio_buffer.speech_started': {
          // Duck, don't kill: dip the narration volume (NOT a hard pause — a
          // suspend audibly glitches on every VAD false-trigger, e.g. speaker
          // echo) so the user can talk over it. The interruption commits at
          // speech_stopped only if the speech sustained past the noise
          // threshold; otherwise volume just comes back. Duck ONLY when audio
          // is actually scheduled.
          bargeStartRef.current = Date.now();
          bargeAudioStartRef.current = typeof evt.audio_start_ms === 'number' ? evt.audio_start_ms : null;
          const ctx = playCtxRef.current;
          if (ctx && playGainRef.current && nextPlayTimeRef.current > ctx.currentTime) {
            duckedRef.current = true;
            playGainRef.current.gain.value = speakerMutedRef.current ? 0 : volumeRef.current * 0.15;
            // Failsafe: never stay ducked longer than 4s — if the provider
            // drops the speech_stopped event, restore volume regardless.
            if (duckTimerRef.current) clearTimeout(duckTimerRef.current);
            duckTimerRef.current = setTimeout(() => { if (duckedRef.current) unduck(); }, 4000);
          }
          setVState('listening');
          break;
        }
        case 'input_audio_buffer.speech_stopped': {
          // How long did they actually speak? Prefer the provider's audio
          // timeline; fall back to wall clock minus the VAD silence tail.
          const speechMs =
            typeof evt.audio_end_ms === 'number' && bargeAudioStartRef.current != null
              ? evt.audio_end_ms - bargeAudioStartRef.current
              : Math.max(0, Date.now() - (bargeStartRef.current || Date.now()) - 700);
          const wasDucked = duckedRef.current;
          unduck(); // resume + clear failsafe (no-op when not ducked)
          const sustained = speechMs >= bargeMinSpeechMsRef.current;
          if (wasDucked && !sustained) {
            // Noise blip — narration continues where it paused.
            setVState(activeResponseIdRef.current ? 'speaking' : 'listening');
            break;
          }
          if (sustained && (wasDucked || activeResponseIdRef.current)) {
            // Real interruption of an in-flight reply — commit the barge-in.
            stopPlayback();
            agentPartialRef.current = '';
            setStreamingText('');
            followupWantedRef.current = false;
            if (activeResponseIdRef.current && ws.readyState === WebSocket.OPEN) {
              try { ws.send(JSON.stringify({ type: 'response.cancel' })); } catch (_) {}
            }
            activeResponseIdRef.current = null;
          }
          setVState('processing');
          // The provider owes a response for this turn — if nothing starts
          // within 15s, the watchdog re-requests once, then surfaces the
          // stall instead of leaving dead air ("walkthrough stuck").
          armWatchdog(0);
          break;
        }
        case 'response.created':
          // A new reply supersedes whatever was ducked — flush the stale
          // audio and make sure the context is running so it can be heard.
          if (duckedRef.current) { unduck(); stopPlayback(); }
          activeResponseIdRef.current = evt.response?.id || true;
          responseHadToolCallsRef.current = false;
          clearWatchdog();
          break;
        case 'response.output_audio.delta':
          if (!activeResponseIdRef.current) break;
          setVState('speaking');
          if (evt.delta) playBase64Audio(evt.delta);
          break;
        case 'response.output_audio_transcript.delta':
          if (!activeResponseIdRef.current) break;
          agentPartialRef.current += (evt.delta || '');
          setStreamingText(agentPartialRef.current);
          break;
        case 'response.output_audio_transcript.done': {
          const text = (evt.transcript || agentPartialRef.current || '').trim();
          setStreamingText('');
          if (text) {
            setMessages((prev) => [...prev, { id: `a-${Date.now()}`, role: 'assistant', content: text }]);
          }
          agentPartialRef.current = '';
          break;
        }
        case 'response.done': {
          activeResponseIdRef.current = null;
          agentPartialRef.current = '';
          setStreamingText('');
          if (responseHadToolCallsRef.current && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            responseHadToolCallsRef.current = false;
            if (pendingToolsRef.current > 0) {
              // Tool executions still in flight — the tool completion path
              // fires the follow-up once every output has been posted.
              followupWantedRef.current = true;
              setVState('thinking');
            } else {
              scheduleFollowup();
            }
          } else {
            setVState('listening');
          }
          break;
        }
        case 'response.cancelled':
          unduck();
          stopPlayback();
          activeResponseIdRef.current = null;
          break;
        case 'conversation.item.input_audio_transcription.completed': {
          const userText = (evt.transcript || '').trim();
          if (userText) {
            setMessages((prev) => [...prev, { id: `u-${Date.now()}`, role: 'user', content: userText }]);
          }
          break;
        }
        case 'response.function_call_arguments.done': {
          const fnName = evt.name;
          const callId = evt.call_id;
          let argsObj = {};
          try { argsObj = evt.arguments ? JSON.parse(evt.arguments) : {}; } catch (_) {}
          responseHadToolCallsRef.current = true;
          pendingToolsRef.current += 1;
          setActiveTool(fnName);
          // Live activity in the transcript — the user sees WHAT is running
          // while they wait, and how long it took (or how it failed).
          const toolMsgId = `t-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
          setMessages((prev) => [...prev, { id: toolMsgId, role: 'tool', content: `⚙ ${fnName}…` }]);
          const t0 = Date.now();
          const result = await executeTool(fnName, argsObj);
          const secs = ((Date.now() - t0) / 1000).toFixed(1);
          setMessages((prev) => prev.map((m) => (m.id === toolMsgId
            ? { ...m, content: result?.error ? `✕ ${fnName} · ${String(result.error).slice(0, 120)}` : `✓ ${fnName} · ${secs}s` }
            : m)));
          try {
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && callId) {
              wsRef.current.send(JSON.stringify({
                type: 'conversation.item.create',
                item: {
                  type: 'function_call_output',
                  call_id: callId,
                  output: JSON.stringify(result || { ok: true }),
                },
              }));
            }
          } catch (_) {}
          pendingToolsRef.current = Math.max(0, pendingToolsRef.current - 1);
          if (pendingToolsRef.current === 0) setActiveTool('');
          if (followupWantedRef.current && pendingToolsRef.current === 0) {
            followupWantedRef.current = false;
            scheduleFollowup();
          }
          break;
        }
        default:
          break;
      }
    };
    ws.onerror = () => {}; // onclose carries the recovery logic
    ws.onclose = (evt) => {
      if (abortedRef.current || wsRef.current !== ws) return;
      wsRef.current = null;
      sessionReadyRef.current = false;
      activeResponseIdRef.current = null;
      pendingToolsRef.current = 0;
      followupWantedRef.current = false;
      stopPlayback();
      agentPartialRef.current = '';
      setStreamingText('');
      // Unexpected drop with the mic still live: reconnect with a fresh
      // ephemeral token (the old one is single-use and ~5 min anyway).
      if (streamRef.current && reconnectsRef.current < MAX_RECONNECTS) {
        reconnectsRef.current += 1;
        setVState('reconnecting');
        setTimeout(async () => {
          if (abortedRef.current || !streamRef.current) return;
          try {
            const data = await fetchSession();
            if (abortedRef.current || !streamRef.current) return;
            connect(data);
          } catch (err) {
            setError(`Voice reconnect failed: ${err.message}`);
            stopVoice();
          }
        }, 500 * reconnectsRef.current);
      } else {
        if (evt && evt.code !== 1000) setError(`Voice ended (${evt.code}${evt.reason ? `: ${evt.reason}` : ''})`);
        stopVoice();
      }
    };
  }, [armWatchdog, clearWatchdog, executeTool, fetchSession, playBase64Audio, scheduleFollowup, sendScreen, setVState, stopPlayback, stopVoice, unduck]);

  const startVoice = useCallback(async () => {
    if (voiceActive) return;
    abortedRef.current = false;
    sessionReadyRef.current = false;
    micBufferRef.current = [];
    playQueueRef.current = [];
    nextPlayTimeRef.current = 0;
    reconnectsRef.current = 0;
    pendingToolsRef.current = 0;
    followupWantedRef.current = false;
    setError(null);
    setVState('connecting');
    setVoiceActive(true);
    try {
      const [micStream, sessionData] = await Promise.all([
        navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        }),
        fetchSession(),
      ]);
      if (abortedRef.current) { micStream.getTracks().forEach((t) => t.stop()); return; }
      streamRef.current = micStream;

      const AC = window.AudioContext || window.webkitAudioContext;
      const captureCtx = new AC({ sampleRate: SAMPLE_RATE });
      captureCtxRef.current = captureCtx;
      const playCtx = new AC({ sampleRate: SAMPLE_RATE });
      playCtxRef.current = playCtx;
      const gain = playCtx.createGain();
      gain.connect(playCtx.destination);
      playGainRef.current = gain;
      applyOutputGain();

      await attachMic(captureCtx, micStream);
      if (abortedRef.current) return;

      startVisualiser(captureCtx, micStream);
      sessionStartRef.current = Date.now();
      setElapsed(0);
      elapsedTimerRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - sessionStartRef.current) / 1000));
      }, 1000);

      connectWs(sessionData);
    } catch (err) {
      console.error('[VoiceAgent] start failed:', err);
      setError(err && err.name === 'NotAllowedError' ? 'Microphone permission denied.' : err.message || 'Failed to start voice');
      stopVoice();
    }
  }, [voiceActive, applyOutputGain, attachMic, connectWs, fetchSession, setVState, startVisualiser, stopVoice]);

  /* ── Audio controls ────────────────────────────────────────────── */
  const setMicMuted = useCallback((m) => {
    if (!streamRef.current) return;
    const track = streamRef.current.getAudioTracks()[0];
    if (!track) return;
    track.enabled = !m;
    mutedRef.current = m;
    setMuted(m);
  }, []);

  const toggleMute = useCallback(() => setMicMuted(!mutedRef.current), [setMicMuted]);

  /* Push-to-talk: while enabled the mic stays muted except when Space is held
     (outside text inputs). */
  useEffect(() => {
    if (!voiceActive || !pttMode) return undefined;
    setMicMuted(true);
    const typing = (e) => {
      const t = e.target;
      return t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
    };
    const down = (e) => {
      if (e.code === 'Space' && !typing(e) && !e.repeat) { e.preventDefault(); setMicMuted(false); }
    };
    const up = (e) => {
      if (e.code === 'Space' && !typing(e)) { e.preventDefault(); setMicMuted(true); }
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
  }, [voiceActive, pttMode, setMicMuted]);

  /* Cmd/Ctrl+Shift+V toggles the panel from anywhere in the app. */
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && (e.key === 'v' || e.key === 'V')) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /* Drag-to-reposition: grab the panel header; position persists. */
  const onHeadPointerDown = useCallback((e) => {
    if (e.target.closest('button')) return; // header buttons still click
    const panel = e.currentTarget.closest('.va-panel');
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    dragRef.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top, w: rect.width };
    const move = (ev) => {
      if (!dragRef.current) return;
      const x = Math.min(Math.max(8, ev.clientX - dragRef.current.dx), window.innerWidth - dragRef.current.w - 8);
      const y = Math.min(Math.max(8, ev.clientY - dragRef.current.dy), window.innerHeight - 60);
      setPanelPos({ x, y });
    };
    const upH = () => {
      dragRef.current = null;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', upH);
      setPanelPos((p) => {
        try { if (p) localStorage.setItem('va-panel-pos', JSON.stringify(p)); } catch (_) {}
        return p;
      });
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', upH);
  }, []);

  const toggleSpeaker = useCallback(() => {
    speakerMutedRef.current = !speakerMutedRef.current;
    setSpeakerMuted(speakerMutedRef.current);
    applyOutputGain();
  }, [applyOutputGain]);

  const onVolumeChange = useCallback((e) => {
    const v = Number(e.target.value);
    volumeRef.current = v;
    setVolume(v);
    applyOutputGain();
  }, [applyOutputGain]);

  /* ── Text chat ─────────────────────────────────────────────────── */
  const sendText = useCallback(async () => {
    const text = chatInput.trim();
    if (!text || chatBusy) return;
    setChatInput('');
    setError(null);
    setMessages((prev) => [...prev, { id: `u-${Date.now()}`, role: 'user', content: text }]);

    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN && sessionReadyRef.current) {
      // Live voice session: inject the text as a user turn; the agent
      // answers aloud through the normal realtime flow.
      if (activeResponseIdRef.current) {
        try { ws.send(JSON.stringify({ type: 'response.cancel' })); } catch (_) {}
        stopPlayback();
        activeResponseIdRef.current = null;
      }
      try {
        ws.send(JSON.stringify({
          type: 'conversation.item.create',
          item: { type: 'message', role: 'user', content: [{ type: 'input_text', text }] },
        }));
        ws.send(JSON.stringify({ type: 'response.create' }));
      } catch (_) {}
      return;
    }

    // No session: text-chat fallback via the backend (same tool registry).
    setChatBusy(true);
    try {
      const history = [...messages, { role: 'user', content: text }]
        .slice(-16)
        .map((m) => ({ role: m.role, content: m.content }));
      const res = await fetch(`${API}/voice/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({
          messages: history,
          page: `${window.location.pathname}${window.location.search}`.slice(0, 300),
          screen: captureScreenOutline({ maxChars: 3500 }),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `chat failed (${res.status})`);
      // Client-executed actions, in order: navigate first, then the hands.
      let wait = 0;
      (data.actions || []).forEach((a) => {
        const path = a?.params?.path;
        if (a.tool === 'navigate_app') {
          if (typeof path === 'string' && isKnownRoute(path)) { navigate(path); wait = 600; }
        } else if (isPageControlTool(a.tool)) {
          setTimeout(() => { try { runPageControl(a.tool, a.params || {}); } catch (_) {} }, wait);
          wait += 900;
        }
      });
      if (data.reply) {
        setMessages((prev) => [...prev, { id: `a-${Date.now()}`, role: 'assistant', content: data.reply }]);
      }
    } catch (err) {
      setError(err.message || 'Chat failed');
    } finally {
      setChatBusy(false);
    }
  }, [chatInput, chatBusy, messages, navigate, stopPlayback]);

  useEffect(() => () => stopVoice(), [stopVoice]);

  if (!API) return null;

  const waiting = voiceState === 'processing' || voiceState === 'thinking';
  // Re-renders every second via the elapsed timer while a session is live,
  // so this counts up without its own interval.
  const thinkSecs = waiting && thinkingSinceRef.current
    ? Math.max(0, Math.floor((Date.now() - thinkingSinceRef.current) / 1000))
    : 0;

  const stateLabel =
    voiceState === 'listening' ? 'Listening' :
    voiceState === 'speaking' ? 'Speaking' :
    waiting ? 'Thinking' :
    voiceState === 'connecting' ? 'Connecting' :
    voiceState === 'reconnecting' ? 'Reconnecting' : 'Idle';

  // Silence should be impossible: while waiting, show WHAT is running and for
  // how long — in the header, on the launcher, and on the minimized pill.
  const liveLabel = activeTool
    ? `Running ${activeTool}…${thinkSecs > 1 ? ` ${thinkSecs}s` : ''}`
    : waiting && thinkSecs > 1 ? `${stateLabel} · ${thinkSecs}s` : stateLabel;

  const lastMsg = messages.length ? messages[messages.length - 1] : null;
  const pillCaption = streamingText
    ? `…${streamingText.slice(-80)}`
    : activeTool
      ? `⚙ ${activeTool}…`
      : (lastMsg ? String(lastMsg.content).slice(0, 80) : '');

  const copyTranscript = () => {
    try {
      navigator.clipboard.writeText(messages.map((m) => `${m.role}: ${m.content}`).join('\n'));
    } catch (_) {}
  };

  return (
    <>
      {!(open && minimized) && (
        <button
          type="button"
          className={`va-launcher ${voiceActive ? 'va-launcher-live' : ''} ${voiceActive && muted ? 'va-launcher-muted' : ''}`}
          onClick={() => { setMinimized(false); setOpen((o) => !o); }}
          title={open ? 'Close voice assistant' : 'Open voice assistant (⌘⇧V)'}
        >
          <span className="va-launcher-orb" aria-hidden />
          <span className="va-launcher-label">
            {voiceActive ? (muted && !pttMode ? `${liveLabel} · Mic muted` : liveLabel) : 'Voice'}
          </span>
        </button>
      )}

      {open && minimized && (
        <div className="va-pill" role="dialog" aria-label="Voice assistant (minimized)">
          <button type="button" className="va-pill-main" onClick={() => setMinimized(false)} title="Expand voice assistant">
            <span className={`va-pill-orb va-pill-orb-${voiceState}`} aria-hidden />
            <span className="va-pill-text">
              <span className="va-pill-state">{voiceActive ? liveLabel : 'Voice paused'}</span>
              {pillCaption && <span className="va-pill-caption">{pillCaption}</span>}
            </span>
          </button>
          {voiceActive && (
            <button
              type="button"
              className={`va-pill-btn ${muted ? 'va-pill-btn-on' : ''}`}
              onClick={toggleMute}
              title={muted ? 'Unmute microphone' : 'Mute microphone'}
            >
              {muted ? 'Unmute' : 'Mute'}
            </button>
          )}
          <button
            type="button"
            className="va-pill-btn"
            onClick={voiceActive ? stopVoice : () => { setOpen(false); setMinimized(false); }}
            title={voiceActive ? 'End the voice session' : 'Close'}
          >
            {voiceActive ? 'End' : '×'}
          </button>
        </div>
      )}

      {open && !minimized && (
        <div
          className="va-panel"
          role="dialog"
          aria-label="Voice assistant"
          style={panelPos ? { left: panelPos.x, top: panelPos.y, right: 'auto', bottom: 'auto' } : undefined}
        >
          <header className="va-head" onPointerDown={onHeadPointerDown} title="Drag to move">
            <span className={`va-pill-orb va-pill-orb-${voiceState}`} aria-hidden />
            <span className="va-head-title">{voiceActive ? liveLabel : 'Voice assistant'}</span>
            <span className="va-head-btns">
              {voiceActive && (
                <button
                  type="button"
                  className={`va-head-btn ${muted ? 'va-head-btn-on' : ''}`}
                  onClick={toggleMute}
                  aria-pressed={muted}
                  title={muted ? 'Unmute microphone' : 'Mute microphone'}
                >
                  {muted ? 'Unmute' : 'Mute'}
                </button>
              )}
              {voiceActive && (
                <button
                  type="button"
                  className={`va-head-btn ${speakerMuted ? 'va-head-btn-on' : ''}`}
                  onClick={toggleSpeaker}
                  aria-pressed={speakerMuted}
                  title={speakerMuted ? 'Unmute the assistant voice' : 'Mute the assistant voice'}
                >
                  {speakerMuted ? 'Spk off' : 'Spk'}
                </button>
              )}
              <button type="button" className="va-head-btn" onClick={() => setMinimized(true)} title="Minimize to pill">
                —
              </button>
              <button type="button" className="va-head-close" onClick={() => setOpen(false)} aria-label="Close" title="Close (⌘⇧V)">×</button>
            </span>
          </header>

          <button
            type="button"
            className={`va-cta ${voiceActive ? 'va-cta-live' : ''}`}
            onClick={voiceActive ? stopVoice : startVoice}
          >
            {voiceActive
              ? `End voice · ${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, '0')}`
              : 'Start voice'}
          </button>

          {voiceActive && (
            <>
              <div className={`va-live va-live-${voiceState}`}>
                <div ref={barsRef} className="va-bars">
                  {Array.from({ length: NUM_BARS }).map((_, i) => <span key={i} className="va-bar" />)}
                </div>
                <button
                  type="button"
                  className={`va-mute ${pttMode ? 'va-mute-on' : ''}`}
                  onClick={() => setPttMode((p) => !p)}
                  aria-pressed={pttMode}
                  title="Push-to-talk: mic stays muted except while you hold Space"
                >
                  {pttMode ? 'Hold Space to talk' : 'Push-to-talk'}
                </button>
              </div>
              <div className="va-audio">
                <input
                  type="range"
                  className="va-vol"
                  min="0"
                  max="1"
                  step="0.05"
                  value={volume}
                  onChange={onVolumeChange}
                  disabled={speakerMuted}
                  aria-label="Assistant volume"
                />
              </div>
            </>
          )}
          {error && <div className="va-error">{error}</div>}

          {messages.length > 0 && (
            <div className="va-conv-tools">
              <button type="button" className="va-conv-tool" onClick={copyTranscript} title="Copy the transcript">Copy</button>
              <button type="button" className="va-conv-tool" onClick={() => setMessages([])} title="Clear the transcript">Clear</button>
              {!voiceActive && <span className="va-conv-hint">continue below in text — context carries over</span>}
            </div>
          )}

          <div ref={scrollRef} className="va-conv">
            {messages.length === 0 && !streamingText && (
              <p className="va-empty">
                Ask about backend status, items, or jobs — the agent can also
                navigate the app while it talks. Talk, or type below.
              </p>
            )}
            {messages.map((m) => (
              <div key={m.id} className={`va-msg va-msg-${m.role}`}>{m.content}</div>
            ))}
            {streamingText && (
              <div className="va-msg va-msg-assistant va-msg-streaming">{streamingText}▍</div>
            )}
            {voiceActive && waiting && !streamingText && (
              <div className="va-msg va-msg-tool">
                {activeTool ? `⚙ ${activeTool}…` : 'Thinking'}{thinkSecs > 1 ? ` · ${thinkSecs}s` : ''}
              </div>
            )}
            {chatBusy && <div className="va-msg va-msg-assistant va-msg-streaming">…</div>}
          </div>

          <form
            className="va-chat"
            onSubmit={(e) => { e.preventDefault(); sendText(); }}
          >
            <input
              className="va-chat-input"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              placeholder={voiceActive ? 'Type instead of talking…' : 'Type a message…'}
              aria-label="Message the assistant"
            />
            <button type="submit" className="va-chat-send" disabled={chatBusy || !chatInput.trim()}>
              Send
            </button>
          </form>
        </div>
      )}
    </>
  );
}
